import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReportParameter } from '../../api/types.js';
import { useApi } from '../../api/client.js';

/**
 * Dynamic form generated from a template's parameters contract.
 *
 * Every ReportParameterKind maps to a typed input:
 *   string      → text
 *   integer     → number (step=1)
 *   float       → number (step=any)
 *   boolean     → checkbox
 *   date        → <input type="date">
 *   timestamp   → <input type="datetime-local">
 *   enum        → single-select <select>
 *   multi_enum  → multi-select (checkbox chip group)
 *
 * Validation:
 *   - required fields with empty values disable the submit in the parent
 *     (parent checks `valid`)
 *   - numeric values are kept as numbers when well-formed, string otherwise
 *     so the API can surface the validation error cleanly
 *
 * Storage: values is a Record<name, unknown> — shape the render API
 * expects for `params`.
 */
export function ParameterForm({
  parameters,
  values,
  onChange,
  compact,
  reportId,
}: {
  parameters: ReportParameter[];
  values: Record<string, unknown>;
  onChange(next: Record<string, unknown>): void;
  compact?: boolean;
  /** Report instance id — enables remote_enum dropdowns (CSAGE-205/236),
   *  whose options are fetched scoped to this report. */
  reportId?: string;
}): JSX.Element {
  // CAP3-8861: never render parameters flagged hidden — they're
  // internal bookkeeping (Canopy_ClientID, Canopy_UserName, etc.) and
  // the render API substitutes the contract default for any caller
  // override. Authors who need to inspect hidden params can do so in
  // the standalone Sage template-edit view (separate surface).
  const visibleParameters = parameters.filter((p) => !p.hidden);
  if (visibleParameters.length === 0) {
    return (
      <p className="text-dim text-sm" style={{ margin: 0 }}>
        This template declares no parameters.
      </p>
    );
  }

  // Names present in this contract — the Report Period widget only
  // auto-fills From/To params that the template actually declares.
  const paramNames = useMemo(
    () => new Set(visibleParameters.map((p) => p.name)),
    [visibleParameters],
  );

  // Merge a partial patch (one or more keys) into the values map. Empty
  // string / undefined removes a key. Used by known-param widgets that
  // set sibling fields (Report Period → From/To).
  const patch = (partial: Record<string, unknown>): void => {
    const next = { ...values };
    for (const [k, v] of Object.entries(partial)) {
      if (v === undefined || v === '') delete next[k];
      else next[k] = v;
    }
    onChange(next);
  };

  return (
    <div className={'param-form' + (compact ? ' param-form--compact' : '')}>
      {visibleParameters.map((p) => (
        <ParameterField
          key={p.name}
          param={p}
          value={values[p.name]}
          contractNames={paramNames}
          {...(reportId ? { reportId } : {})}
          onPatch={patch}
          onChange={(v) => patch({ [p.name]: v })}
        />
      ))}
    </div>
  );
}

/**
 * Returns true when every required parameter has a non-empty value.
 * Exported so the parent can gate submit buttons.
 */
export function parametersAreValid(
  parameters: ReportParameter[],
  values: Record<string, unknown>,
): boolean {
  for (const p of parameters) {
    if (!p.required) continue;
    // CAP3-8861: hidden params don't gate submit even when required —
    // they're enforced server-side from the template default, and the
    // user has no way to fill them in. Asking them to is nonsense.
    if (p.hidden) continue;
    const v = values[p.name];
    if (v === undefined || v === null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
  }
  return true;
}

/**
 * Build an initial values record from a parameters contract, applying
 * each parameter's defaultValue where present. Use this on mount +
 * whenever the template version changes.
 */
export function defaultParameterValues(parameters: ReportParameter[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of parameters) {
    if (p.defaultValue !== undefined && p.defaultValue !== null) {
      out[p.name] = p.defaultValue;
    }
  }
  return out;
}

// ─── Per-field rendering ──────────────────────────────────────────────────

function ParameterField({
  param,
  value,
  contractNames,
  reportId,
  onChange,
  onPatch,
}: {
  param: ReportParameter;
  value: unknown;
  contractNames: Set<string>;
  reportId?: string;
  onChange(v: unknown): void;
  onPatch(partial: Record<string, unknown>): void;
}): JSX.Element {
  const labelId = `param-${param.name}`;
  const isPeriod = isReportPeriodParam(param.name);
  return (
    <div className="param-field" data-required={param.required ? 'yes' : 'no'}>
      <label htmlFor={labelId} className="param-field-label">
        <span className="param-field-name">{param.label || param.name}</span>
        {param.required && (
          <span className="param-required-dot" aria-label="required">
            ●
          </span>
        )}
        <span className="param-field-kind">{isPeriod ? 'period' : param.kind}</span>
      </label>
      {param.help && <div className="param-field-help">{param.help}</div>}
      {isPeriod ? (
        <ReportPeriodInput
          id={labelId}
          value={value}
          contractNames={contractNames}
          onPatch={onPatch}
          onChange={onChange}
        />
      ) : param.kind === 'remote_enum' && reportId ? (
        <RemoteEnumInput
          id={labelId}
          reportId={reportId}
          paramName={param.name}
          value={value}
          onChange={onChange}
        />
      ) : (
        <ParameterInput id={labelId} param={param} value={value} onChange={onChange} />
      )}
    </div>
  );
}

// ─── CSAGE-205: Report Period preset dropdown ─────────────────────────────
//
// input_Report_Period migrated as a plain text box showing the raw code
// (e.g. "PAST_7_DAYS"). Render it as a dropdown of the platform presets
// instead, and — matching legacy Jasper — auto-fill input_From_Date /
// input_To_Date when a preset is picked, using local date math. The dates
// stay editable so the user can still override the window (CUSTOM).
//
// The preset list mirrors apps/api/src/data-sources/report-periods.ts
// (KNOWN_PERIODS). The server remains the source of truth for the range at
// render time; this fill is a convenience + parity affordance.
const REPORT_PERIOD_PRESETS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'PAST_7_DAYS', label: 'Past 7 days' },
  { value: 'PAST_30_DAYS', label: 'Past 30 days' },
  { value: 'PAST_60_DAYS', label: 'Past 60 days' },
  { value: 'PAST_90_DAYS', label: 'Past 90 days' },
  { value: 'PAST_180_DAYS', label: 'Past 180 days' },
  { value: 'PAST_365_DAYS', label: 'Past 365 days' },
  { value: 'THIS_WEEK', label: 'This week' },
  { value: 'LAST_WEEK', label: 'Last week' },
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LAST_MONTH', label: 'Last month' },
  { value: 'MONTH_TO_DATE', label: 'Month to date' },
  { value: 'THIS_YEAR', label: 'This year' },
  { value: 'LAST_YEAR', label: 'Last year' },
  { value: 'YEAR_TO_DATE', label: 'Year to date' },
  { value: 'CUSTOM', label: 'Custom (set dates below)' },
];

const FROM_PARAM = 'input_From_Date';
const TO_PARAM = 'input_To_Date';

function isReportPeriodParam(name: string): boolean {
  return name.toLowerCase() === 'input_report_period';
}

/** Local-date preset → {from,to} as YYYY-MM-DD. Mirrors report-periods.ts
 *  (browser-local; the render pipeline re-resolves authoritatively). Returns
 *  null for CUSTOM (leave the user's dates alone). */
function resolvePresetRange(preset: string): { from: string; to: string } | null {
  const iso = (d: Date): string => {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysAgo = (n: number): Date => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return d;
  };
  switch (preset) {
    case 'TODAY':
      return { from: iso(today), to: iso(today) };
    case 'YESTERDAY':
      return { from: iso(daysAgo(1)), to: iso(daysAgo(1)) };
    // PAST_N anchors from = today - N (matches report-periods.ts / CSAGE-131).
    case 'PAST_7_DAYS':
      return { from: iso(daysAgo(7)), to: iso(today) };
    case 'PAST_30_DAYS':
      return { from: iso(daysAgo(30)), to: iso(today) };
    case 'PAST_60_DAYS':
      return { from: iso(daysAgo(60)), to: iso(today) };
    case 'PAST_90_DAYS':
      return { from: iso(daysAgo(90)), to: iso(today) };
    case 'PAST_180_DAYS':
      return { from: iso(daysAgo(180)), to: iso(today) };
    case 'PAST_365_DAYS':
      return { from: iso(daysAgo(365)), to: iso(today) };
    // Weeks are MONDAY-start (ISO), matching report-periods.ts / CSAGE-131.
    // getDay(): 0=Sun … 6=Sat → days since Monday = (dow + 6) % 7.
    case 'THIS_WEEK': {
      const start = daysAgo((today.getDay() + 6) % 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { from: iso(start), to: iso(end) };
    }
    case 'LAST_WEEK': {
      const thisMonday = daysAgo((today.getDay() + 6) % 7);
      const start = new Date(thisMonday);
      start.setDate(start.getDate() - 7);
      const end = new Date(thisMonday);
      end.setDate(end.getDate() - 1);
      return { from: iso(start), to: iso(end) };
    }
    case 'THIS_MONTH':
    case 'MONTH_TO_DATE': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end =
        preset === 'THIS_MONTH' ? new Date(today.getFullYear(), today.getMonth() + 1, 0) : today;
      return { from: iso(start), to: iso(end) };
    }
    case 'LAST_MONTH': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: iso(start), to: iso(end) };
    }
    case 'THIS_YEAR':
    case 'YEAR_TO_DATE': {
      const start = new Date(today.getFullYear(), 0, 1);
      const end = preset === 'THIS_YEAR' ? new Date(today.getFullYear(), 11, 31) : today;
      return { from: iso(start), to: iso(end) };
    }
    case 'LAST_YEAR': {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { from: iso(start), to: iso(end) };
    }
    default:
      return null; // CUSTOM / unknown → don't touch dates
  }
}

function ReportPeriodInput({
  id,
  value,
  contractNames,
  onPatch,
  onChange,
}: {
  id: string;
  value: unknown;
  contractNames: Set<string>;
  onPatch(partial: Record<string, unknown>): void;
  onChange(v: unknown): void;
}): JSX.Element {
  const current = stringValue(value);
  // Surface an unrecognized migrated value (e.g. legacy free-text) so it
  // isn't silently dropped from the dropdown.
  const known = REPORT_PERIOD_PRESETS.some((p) => p.value === current);
  return (
    <select
      id={id}
      className="select"
      value={current}
      onChange={(e) => {
        const preset = e.target.value;
        if (!preset) return onChange(undefined);
        const range = resolvePresetRange(preset);
        const partial: Record<string, unknown> = { input_Report_Period: preset };
        // Only fill From/To that the template actually declares.
        if (range && contractNames.has(FROM_PARAM)) partial[FROM_PARAM] = range.from;
        if (range && contractNames.has(TO_PARAM)) partial[TO_PARAM] = range.to;
        onPatch(partial);
      }}
    >
      <option value="">(none)</option>
      {!known && current !== '' && <option value={current}>{current} (current)</option>}
      {REPORT_PERIOD_PRESETS.map((p) => (
        <option key={p.value} value={p.value}>
          {p.label}
        </option>
      ))}
    </select>
  );
}

// ─── CSAGE-205/236: remote_enum searchable dropdown ───────────────────────
//
// Options come from a live service at render time, scoped to the report
// instance: GET /reports/:id/parameters/:name/options?q=<search>. Backed by
// the location service (kind=location) or device-service attribute
// definitions (kind=device_attribute) — resolved server-side, so the widget
// just fetches + renders. Typeahead-filtered for large lists; the submitted
// value is the option's `value` (label shown to the user).
interface RemoteOption {
  label: string;
  value: string;
}

function RemoteEnumInput({
  id,
  reportId,
  paramName,
  value,
  onChange,
}: {
  id: string;
  reportId: string;
  paramName: string;
  value: unknown;
  onChange(v: unknown): void;
}): JSX.Element {
  const api = useApi();
  const current = stringValue(value);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<RemoteOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Fetch options on open + whenever the typed query changes (debounced).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = setTimeout(() => {
      const qs = query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
      api
        .fetch<{ options: RemoteOption[] }>(
          `/reports/${reportId}/parameters/${encodeURIComponent(paramName)}/options${qs}`,
        )
        .then((res) => {
          if (cancelled) return;
          setOptions(Array.isArray(res.options) ? res.options : []);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : 'Failed to load options');
          setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, query, reportId, paramName, api]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Label to show for the selected value: prefer a fetched option's label,
  // else the raw code (until options load).
  const selectedLabel = options.find((o) => o.value === current)?.label ?? current;

  return (
    <div className="remote-enum" ref={boxRef} style={{ position: 'relative' }}>
      <input
        id={id}
        className="input"
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        placeholder={current ? '' : 'Select…'}
        value={open ? query : selectedLabel}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
      />
      {current && !open && (
        <button
          type="button"
          className="remote-enum-clear"
          aria-label="Clear"
          title="Clear"
          onClick={() => onChange(undefined)}
          style={{ position: 'absolute', right: 6, top: 6 }}
        >
          ✕
        </button>
      )}
      {open && (
        <div className="remote-enum-menu" role="listbox">
          {loading && <div className="remote-enum-status">Loading…</div>}
          {error && <div className="remote-enum-status remote-enum-status--error">{error}</div>}
          {!loading && !error && options.length === 0 && (
            <div className="remote-enum-status">No matches</div>
          )}
          {!loading &&
            !error &&
            options.map((o) => (
              <button
                type="button"
                key={o.value}
                role="option"
                aria-selected={o.value === current}
                className={
                  'remote-enum-option' + (o.value === current ? ' remote-enum-option--active' : '')
                }
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function ParameterInput({
  id,
  param,
  value,
  onChange,
}: {
  id: string;
  param: ReportParameter;
  value: unknown;
  onChange(v: unknown): void;
}): JSX.Element {
  switch (param.kind) {
    case 'string':
      return (
        <input
          id={id}
          className="input input--sm"
          value={stringValue(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={typeof param.defaultValue === 'string' ? param.defaultValue : ''}
        />
      );
    case 'integer':
      return (
        <input
          id={id}
          type="number"
          step={1}
          className="input input--sm"
          value={numberInputValue(value)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return onChange(undefined);
            const n = parseInt(raw, 10);
            onChange(Number.isFinite(n) ? n : raw);
          }}
        />
      );
    case 'float':
      return (
        <input
          id={id}
          type="number"
          step="any"
          className="input input--sm"
          value={numberInputValue(value)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return onChange(undefined);
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : raw);
          }}
        />
      );
    case 'boolean':
      return (
        <label className="param-bool">
          <input
            id={id}
            type="checkbox"
            checked={value === true || value === 'true'}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{value === true || value === 'true' ? 'True' : 'False'}</span>
        </label>
      );
    case 'date':
      return (
        <input
          id={id}
          type="date"
          className="input input--sm"
          value={stringValue(value).slice(0, 10)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'timestamp':
      return (
        <input
          id={id}
          type="datetime-local"
          className="input input--sm"
          value={toDateTimeLocal(value)}
          onChange={(e) =>
            onChange(e.target.value ? new Date(e.target.value).toISOString() : undefined)
          }
        />
      );
    case 'enum':
      return (
        <select
          id={id}
          className="select"
          value={stringValue(value)}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">(none)</option>
          {(param.choices ?? []).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      );
    case 'multi_enum':
      return <MultiEnumInput id={id} param={param} value={value} onChange={onChange} />;
    case 'remote_enum':
      // Fallback only — reached when there is no report scope to resolve
      // options against (e.g. the template-authoring preview). The live
      // searchable dropdown (RemoteEnumInput) is used in the report viewer,
      // where a reportId is available.
      return (
        <input
          id={id}
          className="input"
          type="text"
          value={stringValue(value)}
          placeholder="Set a value (dropdown available in the report viewer)"
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
  }
}

function MultiEnumInput({
  id,
  param,
  value,
  onChange,
}: {
  id: string;
  param: ReportParameter;
  value: unknown;
  onChange(v: unknown): void;
}): JSX.Element {
  const selected = useMemo(() => {
    if (Array.isArray(value)) return new Set(value.map(String));
    if (typeof value === 'string' && value) return new Set(value.split(',').map((s) => s.trim()));
    return new Set<string>();
  }, [value]);

  const toggle = (v: string): void => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next.size > 0 ? [...next] : undefined);
  };

  return (
    <div className="param-multi" id={id}>
      {(param.choices ?? []).map((c) => {
        const on = selected.has(c.value);
        return (
          <button
            key={c.value}
            type="button"
            className={'chip' + (on ? ' chip--on' : '')}
            onClick={() => toggle(c.value)}
            aria-pressed={on}
          >
            {c.label}
          </button>
        );
      })}
      {selected.size > 0 && (
        <button type="button" className="chip chip--clear" onClick={() => onChange(undefined)}>
          Clear
        </button>
      )}
    </div>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────

function stringValue(v: unknown): string {
  if (v === undefined || v === null) return '';
  return typeof v === 'string' ? v : String(v);
}

function numberInputValue(v: unknown): number | string {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'number') return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
}

function toDateTimeLocal(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DDTHH:MM (drop seconds + TZ suffix for the native input)
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── Hook for parent convenience ──────────────────────────────────────────

/**
 * Keeps a values map seeded with defaults whenever `parameters`
 * changes (e.g. after publishing a new version that adds a param).
 * Preserves user-supplied values for params that still exist.
 */
export function useParameterValues(
  parameters: ReportParameter[],
  /** Initial overrides (e.g. a Report instance's default_parameters).
   * Applied on first seed and when the contract shape changes; user
   * edits to existing parameter names are preserved across contract
   * changes, but a reset() reverts to `contractDefaults ∪ overrides`. */
  initialOverrides: Record<string, unknown> = {},
): {
  values: Record<string, unknown>;
  ready: boolean;
  setValues: (v: Record<string, unknown>) => void;
  reset: () => void;
} {
  const seed = (): Record<string, unknown> => ({
    ...defaultParameterValues(parameters),
    ...initialOverrides,
  });
  const [values, setValues] = useState<Record<string, unknown>>(seed);

  // Re-sync when the parameter contract changes shape (keep compatible values).
  const contractKey = useMemo(
    () => parameters.map((p) => `${p.name}:${p.kind}`).join('|'),
    [parameters],
  );
  useEffect(() => {
    const merged: Record<string, unknown> = seed();
    setValues((prev) => {
      const out = { ...merged };
      for (const p of parameters) {
        if (p.name in prev) out[p.name] = prev[p.name];
      }
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractKey]);

  // Re-seed when the overrides object identity changes (e.g. switching
  // between Report instances). Stringify to avoid needless churn on
  // equal-by-value objects that different renders produce anew.
  const overridesKey = useMemo(() => JSON.stringify(initialOverrides), [initialOverrides]);
  useEffect(() => {
    setValues(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overridesKey]);

  const seedKey = `${contractKey}::${overridesKey}`;
  const [appliedSeedKey, setAppliedSeedKey] = useState<string | null>(null);
  useEffect(() => {
    setAppliedSeedKey(seedKey);
  }, [seedKey]);

  return {
    values,
    ready: appliedSeedKey === seedKey,
    setValues,
    reset: () => setValues(seed()),
  };
}
